/**
 * @voltxampere/engine — Formula template: RC low-pass filter (Sprint 56b)
 *
 * Subgraph: V_in → R → V_out → C → GND.
 *
 *     V_in ── R ── V_out ── C ── GND
 *
 * Output: cutoff frequency f_c = 1 / (2π R C). The detector
 * surfaces both the symbolic transfer function magnitude and the
 * numerical f_c in Hz.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { asNumber, buildNetIndex, componentsOfType, findVoltageSourceDriving, formatLatexValue, isGroundNet, netOf, } from "../helpers.js";
const SYMBOLIC = "f_c = \\frac{1}{2\\pi R C}";
export const rcFilterTemplate = {
    id: "rc-filter",
    specificity: "medium",
    displayNameKey: { tr: "formula.rc-filter.name", en: "formula.rc-filter.name" },
    match(circuit, selected) {
        const resistors = componentsOfType(circuit, "resistor", selected);
        const capacitors = componentsOfType(circuit, "capacitor", selected);
        if (resistors.length === 0 || capacitors.length === 0)
            return null;
        const netIndex = buildNetIndex(circuit);
        for (const r of resistors) {
            const rP1 = netOf(r, "p1");
            const rP2 = netOf(r, "p2");
            if (rP1 === null || rP2 === null)
                continue;
            for (const c of capacitors) {
                const cP1 = netOf(c, "p1");
                const cP2 = netOf(c, "p2");
                if (cP1 === null || cP2 === null)
                    continue;
                // Cap one terminal must be ground; the other is V_out.
                let vOut = null;
                let capGround = false;
                if (isGroundNet(circuit, cP2)) {
                    vOut = cP1;
                    capGround = true;
                }
                else if (isGroundNet(circuit, cP1)) {
                    vOut = cP2;
                    capGround = true;
                }
                if (!capGround || vOut === null)
                    continue;
                // The resistor must have one terminal on V_out and the other
                // driven by a voltage source.
                let vIn = null;
                if (rP1 === vOut)
                    vIn = rP2;
                else if (rP2 === vOut)
                    vIn = rP1;
                if (vIn === null)
                    continue;
                const vSource = findVoltageSourceDriving(circuit, vIn);
                if (vSource === null)
                    continue;
                // Disallow extra current paths off V_out (would change the
                // filter response). High-Z probes (op-amp non-inverting) are
                // permitted.
                const vOutTerminals = netIndex.get(vOut) ?? [];
                const HIGH_Z_PROBE_TYPES = new Set(["op-amp", "opamp", "voltmeter"]);
                const extras = vOutTerminals.filter((t) => t.component.id !== r.id &&
                    t.component.id !== c.id &&
                    !HIGH_Z_PROBE_TYPES.has(t.component.type));
                if (extras.length > 0)
                    continue;
                const rVal = asNumber(r.params.resistance);
                const cVal = asNumber(c.params.capacitance);
                if (rVal === null || cVal === null || rVal === 0 || cVal === 0)
                    continue;
                const fc = 1 / (2 * Math.PI * rVal * cVal);
                const rLatex = formatLatexValue(rVal, "\\Omega");
                const cLatex = formatLatexValue(cVal, "F");
                const fcLatex = formatLatexValue(fc, "Hz");
                return {
                    templateId: "rc-filter",
                    involvedComponents: [r.id, c.id, vSource.id],
                    involvedNets: [vIn, vOut, cP2 === vOut ? cP1 : cP2],
                    symbolic: SYMBOLIC,
                    numeric: {
                        formula: `f_c = \\frac{1}{2\\pi \\cdot ${rLatex} \\cdot ${cLatex}} = ${fcLatex}`,
                        result: fc,
                        unit: "Hz",
                    },
                    specificity: "medium",
                };
            }
        }
        return null;
    },
};
//# sourceMappingURL=rc-filter.js.map